import { useCallback, useMemo, useState } from "react";
import { message } from "antd";
import {
  getNotifications,
  markNotificationAsRead,
  updateReadAllNotification,
} from "../../api/notification/notification";
import { errorMessage } from "../../lib/apiError";

const PAGE_SIZE = 10;

/**
 * State cho khay thông báo — trước đây là `redux/notification/notification.js`.
 *
 * Chỉ `HeaderConek` dùng, và danh sách nạp thêm theo kiểu "Xem thêm" (append),
 * nên đây là state cục bộ của một component, không phải state toàn cục. Đưa vào
 * hook để `HeaderConek` không phải giữ 6 useState + 4 handler.
 *
 * `unreadCount` tính từ danh sách thay vì để một field riêng trong state — bớt
 * được một nguồn sai lệch (slice cũ phải cập nhật tay ở cả hai reducer).
 */
export function useNotifications() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const unreadCount = useMemo(
    () => items.filter((n) => n.is_read === 0).length,
    [items]
  );
  const hasMore = items.length < total;

  const fetchPage = useCallback(async (nextPage) => {
    const isFirst = nextPage === 1;
    if (isFirst) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    try {
      const data = await getNotifications({ currentPage: nextPage, pageSize: PAGE_SIZE });
      const rows = data?.data || [];
      // Trang 1 = thay danh sách; trang sau = nối thêm.
      setItems((prev) => (data?.current_page === 1 ? rows : [...prev, ...rows]));
      setPage(data?.current_page ?? nextPage);
      setTotal(data?.total ?? 0);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const reload = useCallback(() => fetchPage(1), [fetchPage]);
  const loadMore = useCallback(() => fetchPage(page + 1), [fetchPage, page]);

  /** Đánh dấu đã đọc rồi cập nhật cục bộ — không cần gọi lại cả danh sách. */
  const markRead = useCallback(async (id) => {
    try {
      await markNotificationAsRead(id);
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))
      );
    } catch (e) {
      message.error(errorMessage(e));
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      const res = await updateReadAllNotification();
      message.success(res?.message || "Đã đánh dấu tất cả là đã đọc");
      await fetchPage(1);
    } catch (e) {
      message.error(errorMessage(e));
    }
  }, [fetchPage]);

  return {
    items,
    unreadCount,
    total,
    hasMore,
    loading,
    loadingMore,
    error,
    reload,
    loadMore,
    markRead,
    markAllRead,
  };
}
